import { useEffect, useRef, useState } from 'react'
import { NoColorSpace, TangentSpaceNormalMap, Group, Texture, MeshPhysicalMaterial, type Material } from 'three'
import { useThree, useFrame } from '@react-three/fiber'
import { reportLoadingStage, setLoadingProgress } from '../loadingSplash'

const LIGHT_MAP_MOD = 1.5
const BATCH_SIZE = 1
const ENV_INTENSITY_X3 = 1.0
const ENV_INTENSITY_X2 = 0.4
const ENV_INTENSITY_X1 = 0.1

// Progressively prepares the loaded room scene: per-material colour-space and
// envmap fixups, anisotropy, emissive->lightmap conversion, then texture and
// geometry upload. Work is spread a small batch per frame so first paint isn't
// blocked; once the queue drains it kicks off an async shader compile and flips
// to ready (calling onReady). Returns whether the scene is ready to be shown.
export function useSceneProcessing(
  scene: Group,
  envMap: Texture,
  onReady: () => void,
  patchMaterial?: (mat: Material) => void,
  prepare?: (gl: import('three').WebGLRenderer) => Promise<void>,
): boolean {
  const { gl, camera, scene: rootScene } = useThree()
  const meshQueueRef = useRef<any[]>([])
  const totalMeshesRef = useRef(0)
  const seenTexRef = useRef(new Set<Texture>())
  const maxAnisoRef = useRef(0)
  const processingDoneRef = useRef(true)
  const [sceneReady, setSceneReady] = useState(false)

  useEffect(() => {
    if (!envMap) return
    maxAnisoRef.current = Math.min(gl.capabilities.getMaxAnisotropy(), 4)
    const meshes: any[] = []
    scene.traverse((child: any) => { if (child.isMesh) meshes.push(child) })
    meshQueueRef.current = meshes
    totalMeshesRef.current = meshes.length
    seenTexRef.current = new Set()
    processingDoneRef.current = false
    return () => { processingDoneRef.current = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envMap])

  useFrame(() => {
    if (processingDoneRef.current) return

    const meshes = meshQueueRef.current
    if (meshes.length === 0) {
      processingDoneRef.current = true
      // Stage 3: shader compile + projector warm-up.
      reportLoadingStage('compile', 0)
      // Compile every (patched) scene material, then let the projector warm up
      // its offscreen pass + render target, so the first visible frame is stutter
      // free. Only flip to ready once both have finished.
      gl.compileAsync(rootScene, camera)
        .then(() => prepare?.(gl))
        .then(() => {
          setLoadingProgress(1)
          setSceneReady(true)
          onReady()
        })
      return
    }

    const seen = seenTexRef.current
    const maxAniso = maxAnisoRef.current
    const setAniso = (tex: any) => {
      if (tex) { tex.anisotropy = maxAniso; tex.needsUpdate = true }
    }

    const batch = meshes.splice(0, BATCH_SIZE)
    for (const child of batch) {
      child.castShadow = true
      child.receiveShadow = true
      if (!child.geometry.attributes.normal) child.geometry.computeVertexNormals()
      ;(gl as any).initGeometry?.(child.geometry)
      if (!child.material) continue

      const mats: MeshPhysicalMaterial[] = Array.isArray(child.material) ? child.material : [child.material]
      mats.forEach((mat) => {
        mat.transmission = 0
        mat.side = 2
        mat.flatShading = false
        mat.aoMapIntensity = 1
        setAniso(mat.map)
        if (mat.normalMap) {
          mat.normalMap.colorSpace = NoColorSpace
          mat.normalMapType = TangentSpaceNormalMap
        }
        if (mat.roughnessMap) mat.roughnessMap.colorSpace = NoColorSpace
        if (mat.aoMap) mat.aoMap.colorSpace = NoColorSpace
        if (mat.name?.includes('ENVX3')) {
          mat.envMap = envMap
          mat.envMapIntensity = ENV_INTENSITY_X3
        }
        if (mat.name?.includes('ENVX2')) {
          mat.envMap = envMap
          mat.envMapIntensity = ENV_INTENSITY_X2
        }
        if (mat.name?.includes('ENVX1')) {
          mat.envMap = envMap
          mat.envMapIntensity = ENV_INTENSITY_X1
        }

        const slots = [mat.map, mat.normalMap, mat.roughnessMap, mat.metalnessMap,
          mat.aoMap, mat.emissiveMap]
        for (const tex of slots) {
          if (tex && !seen.has(tex)) { seen.add(tex); gl.initTexture(tex) }
        }

        if (mat.emissiveMap) {
          const base = mat.emissiveIntensity
          mat.lightMap = mat.emissiveMap
          mat.lightMapIntensity = base * Math.PI * LIGHT_MAP_MOD
          mat.emissiveMap = null
          mat.emissiveIntensity = 0
        }

        // Wire in the komorebi projection before the final compile so its shader
        // patch is part of the single compileAsync — no extra recompile.
        patchMaterial?.(mat)
      })
    }

    // Stage 2: per-mesh material/texture/geometry preparation.
    const total = totalMeshesRef.current
    reportLoadingStage('prepare', total > 0 ? (total - meshes.length) / total : 1)
  })

  return sceneReady
}
